import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, FolderTree, Maximize2 } from 'lucide-react';
import { DeliverablesTree } from './DeliverablesTree';
import { FileViewerModal } from './FileViewerModal';
import { highlightLines, langLabel, langOf, tokenClass } from './codeHighlight';

export interface ProjectFile {
  path: string;
  content: string;
}

// Files most worth showing first when the panel opens.
const PREFERRED_ENTRY = ['src/App.tsx', 'src/App.jsx', 'src/main.tsx', 'src/main.jsx', 'index.html', 'package.json'];

function formatSize(n: number) {
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`;
}

/**
 * Saved-app project browser: folder tree on the left, syntax-highlighted
 * preview of the selected file on the right, with copy and an "expand" into
 * the full-screen FileViewerModal.
 */
export function ProjectFilesPanel({ files }: { files: ProjectFile[] }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (selected && files.some((f) => f.path === selected)) return;
    const entry = PREFERRED_ENTRY.find((p) => files.some((f) => f.path === p)) ?? files[0]?.path ?? null;
    setSelected(entry);
  }, [files, selected]);

  const file = files.find((f) => f.path === selected);
  const lang = langOf(selected ?? '');
  const lines = useMemo(() => (file ? highlightLines(file.content, lang) : []), [file, lang]);
  const totalSize = useMemo(() => files.reduce((n, f) => n + f.content.length, 0), [files]);
  const crumbs = (selected ?? '').split('/');

  const copy = async () => {
    if (!file) return;
    try {
      await navigator.clipboard.writeText(file.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  useEffect(() => setCopied(false), [selected]);

  return (
    <section className="rounded-xl border border-khiimori-border/10 bg-[#0d0f16] overflow-hidden">
      <header className="flex items-center gap-3 px-4 h-12 border-b border-[#1f2330] bg-[#11141d]">
        <FolderTree className="w-4 h-4 text-primary-300" />
        <h2 className="text-sm font-semibold text-white">Project files</h2>
        <span className="text-[11px] text-gray-500 tabular-nums">
          {files.length} files · {formatSize(totalSize)}
        </span>
      </header>

      <div className="flex flex-col md:flex-row h-[560px]">
        {/* Tree */}
        <div className="md:w-64 shrink-0 border-b md:border-b-0 md:border-r border-[#1f2330] overflow-y-auto px-2 py-2 max-h-48 md:max-h-none custom-scrollbar bg-[#0b0d13]">
          <DeliverablesTree files={files} onOpen={setSelected} selectedPath={selected} />
        </div>

        {/* Preview */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 px-4 h-10 border-b border-[#1f2330] bg-[#0f1118]">
            <nav className="flex items-center gap-1 min-w-0 font-mono text-xs" aria-label="File path">
              {crumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1 min-w-0">
                  {i > 0 && <span className="text-gray-600">/</span>}
                  <span className={`truncate ${i === crumbs.length - 1 ? 'text-white font-semibold' : 'text-gray-500'}`}>{c}</span>
                </span>
              ))}
            </nav>
            {file && <span className="text-[10px] text-gray-500 font-mono shrink-0">{lines.length} lines · {formatSize(file.content.length)} · {langLabel[lang]}</span>}
            <div className="ml-auto flex items-center gap-1 shrink-0">
              <button type="button" onClick={copy} disabled={!file} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-gray-400 hover:bg-[#1f2330] hover:text-gray-200 disabled:opacity-40">
                {copied ? <Check className="w-3.5 h-3.5 text-success-500" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
              </button>
              <button type="button" onClick={() => setExpanded(true)} disabled={!file} title="Open full screen" className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-gray-400 hover:bg-[#1f2330] hover:text-gray-200 disabled:opacity-40">
                <Maximize2 className="w-3.5 h-3.5" /> Expand
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto bg-[#0a0b10] custom-scrollbar">
            {file ? (
              <div className="py-2 font-mono text-[12.5px] leading-6 min-w-fit">
                {lines.map((toks, i) => (
                  <div key={i} className="flex hover:bg-white/[0.02]">
                    <span className="select-none sticky left-0 bg-[#0a0b10] text-right pl-4 pr-4 text-[#3b3f51] tabular-nums shrink-0" style={{ width: `${String(lines.length).length + 3}ch` }}>
                      {i + 1}
                    </span>
                    <span className="pr-6 whitespace-pre">
                      {toks.length === 0 ? ' ' : toks.map((t, j) => <span key={j} className={tokenClass[t.kind]}>{t.text}</span>)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-sm text-gray-500">Select a file to preview it.</div>
            )}
          </div>
        </div>
      </div>

      {expanded && selected && (
        <FileViewerModal files={files} selectedPath={selected} onSelect={setSelected} onClose={() => setExpanded(false)} />
      )}
    </section>
  );
}
